import React, { useState, Suspense } from "react";
import InputComponent from "../components/input-component/input-component";
import CompilationErrorsBox from "../components/compilation-errors";
import useStyles from "../components/styles";
import AutomaticTestingToolHeader from "../components/automatic-testing-tool-header";
import Disclaimer from "../components/disclaimer";
import store from "src/store";
import { requestJSON } from "src/utils/request-module";
import InputText from "src/components/input-text";

export default function VerifyContractByteCode() {
  const title = "Verify contract byte-code";
  const description =
    "This component allows users to verify the compiled bytecode of their smart contract against the bytecode available on Etherscan. It displays both bytecodes side by side and indicates whether they match.";

  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [compiledBytecode, setCompiledBytecode] = useState(null);
  const [etherscanBytecode, setEtherscanBytecode] = useState(null);
  const [files, setFiles] = useState([]);
  const [compilerVersion, setCompilerVersion] = useState("");
  const [mainContract, setMainContract] = useState("");
  const [compilerSettings, setCompilerSettings] = useState("");
  const [mainFile, setMainFile] = useState("");
  const [selectedFilePath, setSelectedFilePath] = useState("");
  const [importPopoverAnchorEl, setImportPopoverAnchorEl] = useState(null);
  const [response, setResponse] = useState({ errors: [], test_results: [] });
  const [contractAddress, setContractAddress] = useState("");
  const [contractAddressHasError, setContractAddressHasError] = useState(false);
  const [contractAddressErrorText, setContractAddressErrorText] = useState("");

  const demoBytecode = "60606040526040604051908101"; // Example demo bytecode

  const fetchBytecodeFromEtherscan = async (contractAddress) => {
    if (!contractAddress) {
      return demoBytecode;
    }

    let lastblock = await store.getLatestBlock()

    const etherscanResponse = await store.getBytecodeFromEtherscan(contractAddress, lastblock.number);
    const bytecode = etherscanResponse.bytecode || demoBytecode;
    return bytecode.startsWith('0x') ? bytecode.slice(2) : bytecode;
  };

  const submitCode = async () => {
    const addressRegex = /^0x[a-fA-F0-9]{40}$/;

    if (!contractAddress) {
      setContractAddressHasError(true);
      setContractAddressErrorText("Contract address is required.");
      // return;
    }

    if (!addressRegex.test(contractAddress)) {
      setContractAddressHasError(true);
      setContractAddressErrorText("Invalid Ethereum address format.");
    }

    try {
      setIsSubmitLoading(true);
      setCompiledBytecode(null);
      setEtherscanBytecode(null);

      const mainFileName = mainFile || "main.sol";

      var {bytecode}  = await store.getDeployedByCode({
        files,
        mainContract,
        mainFile: mainFileName,
        compilerVersion,
        compilerSettings,
      });


      setCompiledBytecode(bytecode);
      let etherscanBytecode=''
      if(addressRegex.test(contractAddress)){
        etherscanBytecode = await fetchBytecodeFromEtherscan(
          contractAddress
        );
      }

      setEtherscanBytecode(etherscanBytecode);

      setResponse(response);
    } catch (err) {
      console.error("Error during bytecode verification:", err);
    } finally {
      setIsSubmitLoading(false);
    }
  };

  const highlightDifferences = (str1, str2) => {
    const maxLength = Math.max(str1.length, str2.length);
    let result1 = "";
    let result2 = "";

    for (let i = 0; i < maxLength; i++) {
      const char1 = str1[i] || "";
      const char2 = str2[i] || "";

      if (char1 !== char2) {
        result1 += `<span style="background-color: yellow;">${char1}</span>`;
        result2 += `<span style="background-color: yellow;">${char2}</span>`;
      } else {
        result1 += char1;
        result2 += char2;
      }
    }

    return { highlighted1: result1, highlighted2: result2 };
  };

  const isMatched = compiledBytecode?.slice(0,-43*2) === etherscanBytecode?.slice(0,-43*2);

  console.log("contractAddressHasError", contractAddressHasError);
  console.log("contractAddressErrorText", contractAddressErrorText);

  return (
    <div
      css={`
        padding: 35px;
        background: #f6f6fa;
        min-height: 100vh;
      `}
    >
      <Suspense fallback={null}>
        <AutomaticTestingToolHeader title={title} description={description} />
        <Disclaimer />
        <hr
          css={`
            margin-top: 15px;
            border: 1px solid #eaeaf2;
          `}
        />
        <div
          css={`
            margin-top: 25px;
            margin-bottom: 25px;
          `}
        >
          <InputComponent
            skipSampleProjects
            onX={submitCode}
            ctaText="Scan"
            showValidationBtn={false}
            files={files}
            setFiles={setFiles}
            isLoading={isSubmitLoading}
            compilerVersion={compilerVersion}
            setCompilerVersion={setCompilerVersion}
            mainContract={mainContract}
            compilerSettings={compilerSettings}
            setCompilerSettings={setCompilerSettings}
            setMainFile={setMainFile}
            mainFile={mainFile}
            setMainContract={setMainContract}
            selectedFilePath={selectedFilePath}
            setSelectedFilePath={setSelectedFilePath}
            importPopoverAnchorEl={importPopoverAnchorEl}
            setImportPopoverAnchorEl={setImportPopoverAnchorEl}
            setContractAddress={setContractAddress}
          />
        </div>
        <div
          css={`
            margin-top: 25px;
            margin-bottom: 15px;
            width: 50%;
          `}
        >
          <InputText
            id={"contractAddress"}
            label={"Contract Address"}
            value={contractAddress}
            disabled={false}
            autoFocus={true}
            onChange={(value) => {
              setContractAddress(value);
              setContractAddressHasError(false);
              setContractAddressErrorText("");
            }}
            hasError={contractAddressHasError}
            errorText={contractAddressErrorText}
          />
        </div>
        <div
          css={`
            margin-bottom: 25px;
            position: relative;
          `}
        >
          {response.errors.length > 0 && (
            <CompilationErrorsBox compilationErrors={response.errors} />
          )}
          {compiledBytecode  && (
            <>
              <div
                css={`
                  margin-bottom: 10px;
                  padding: 10px;
                  background-color: ${isMatched ? "green" : "red"};
                  color: white;
                  text-align: center;
                `}
              >
                {isMatched ? "Matched" : "Not Matched"}
              </div>
              <div
                css={`
                  display: flex;
                  justify-content: space-between;
                  border: 1px solid #ccc;
                `}
              >
                <div
                  css={`
                    width: 50%;
                    padding: 10px;
                    border-right: 1px solid #ccc;
                  `}
                >
                  <h3>Compiled Bytecode:</h3>
                  <pre
                    css={`
                      white-space: pre-wrap;
                      word-break: break-all;
                      overflow-wrap: break-word;
                    `}
                    dangerouslySetInnerHTML={{ __html: compiledBytecode?.slice(0,-43*2) }}
                  />
                  <h3>Compiled Metadata:</h3>
                  <pre
                    css={`
                      white-space: pre-wrap;
                      word-break: break-all;
                      overflow-wrap: break-word;
                    `}
                    dangerouslySetInnerHTML={{ __html: compiledBytecode?.slice(-43*2) }}
                  />
                </div>
                <div
                  css={`
                    width: 50%;
                    padding: 10px;
                  `}
                >
                  <h3>On-chain Bytecode:</h3>
                  <pre
                    css={`
                      white-space: pre-wrap;
                      word-break: break-all;
                      overflow-wrap: break-word;
                    `}
                    dangerouslySetInnerHTML={{ __html: etherscanBytecode?.slice(0,-44*2) }}
                  />
                  <h3>On-chain Metadata:</h3>
                  <pre
                    css={`
                      white-space: pre-wrap;
                      word-break: break-all;
                      overflow-wrap: break-word;
                    `}
                    dangerouslySetInnerHTML={{ __html: etherscanBytecode?.slice(-44*2) }}
                  />
                </div>
              </div>
            </>
          )}
          {isSubmitLoading && <div>Loading...</div>}
        </div>
      </Suspense>
    </div>
  );
}
